import path from 'node:path';
import crypto from 'node:crypto';
import fs from 'node:fs';
import mime from 'mime';
import pLimit from 'p-limit';
import { useAsyncCallback } from '@wix/cli-ui-kit';
import { useBackendAsAServiceClient } from '@wix/backend-as-a-service-client';
import type {
  BackendFile,
  StaticFileMetadata,
} from '@wix/backend-as-a-service-client';
import { useProjectModel } from '../../../model.js';

interface FileData {
  path: string;
  relativePath: string;
}

interface DeployFiles {
  client: FileData[];
  server: FileData[];
}

export function useDeployFiles() {
  const {
    model: {
      projectFolder,
      config: { appId },
    },
  } = useProjectModel();

  const backendAsAServiceClient = useBackendAsAServiceClient();

  return useAsyncCallback(async (_, files: DeployFiles) => {
    const clientFilesMetadata = await Promise.all(
      files.client.map(getStaticFileMetadata)
    );

    const { appDeployment, staticFilesUploadUrls, uploadAuthToken } =
      await backendAsAServiceClient.createAppDeployment(
        appId,
        clientFilesMetadata
      );

    const uploadLimiter = pLimit(3);
    let lastUploadAuthToken = uploadAuthToken;

    if (staticFilesUploadUrls?.length && lastUploadAuthToken) {
      const uploadPromises = staticFilesUploadUrls.map(
        ({ uploadUrl, staticFileMetadata }) => {
          return uploadLimiter(async () => {
            const formData = await buildFormData(
              projectFolder,
              staticFileMetadata
            );

            const response = await backendAsAServiceClient.postForm(
              uploadUrl,
              lastUploadAuthToken,
              formData
            );

            const responseUploadToken = response.result?.jwt;

            if (responseUploadToken) {
              lastUploadAuthToken = responseUploadToken;
            }

            return responseUploadToken;
          });
        }
      );

      await Promise.all(uploadPromises);
    }

    const backendFiles = await Promise.all(
      files.server.map(getBackendFileData)
    );

    const completeAppDeployment =
      await backendAsAServiceClient.completeAppDeployment(
        {
          ...appDeployment,
          files: backendFiles,
        },
        lastUploadAuthToken
      );

    return {
      deploymentId: completeAppDeployment.id,
      deploymentBaseUrl: completeAppDeployment.deploymentBaseUrl,
    };
  });
}

async function getStaticFileMetadata(
  file: FileData
): Promise<StaticFileMetadata> {
  const stats = await fs.promises.stat(file.path);
  const fileContent = await fs.promises.readFile(file.path);
  const hash = crypto.createHash('md5').update(fileContent).digest('hex');

  return {
    size: stats.size,
    hash,
    contentType: mime.getType(file.path),
    path: `/${file.relativePath}`,
  };
}

async function getBackendFileData(file: FileData): Promise<BackendFile> {
  const buffer = await fs.promises.readFile(file.path);
  return { path: file.relativePath, content: buffer.toString('base64') };
}

async function buildFormData(
  projectFolder: string,
  staticFileMetadata: StaticFileMetadata
) {
  const form = new FormData();
  const fileContents = await fs.promises.readFile(
    path.join(projectFolder, 'dist', staticFileMetadata.path)
  );

  form.append(
    staticFileMetadata.hash,
    new File([fileContents.toString('base64')], staticFileMetadata.hash, {
      type: staticFileMetadata.contentType!,
    }),
    staticFileMetadata.hash
  );

  return form;
}
